/**
 * The sync loop shared by the page (syncEngine.js) and the service worker
 * (backgroundSync.js, for syncing while the app is closed). It touches only
 * IndexedDB and the network functions it is given, so it runs in either.
 *
 * Sends the outbox to POST /api/sync in chunks, folds each reply into the
 * saved survey tree (localTree.reconcile), records problems, and finally
 * refreshes the tree from the server. Both callers hold the same Web Lock, so
 * the page and the service worker never send the same outbox at once.
 */
import { applyOperations, reconcile } from './localTree';
import { addProblems, readOutbox, readResponse, removeFromOutbox, saveMeta, saveResponse } from './offlineStore';

export const CHUNK = 200;
export const SYNC_LOCK = 'mangrove-sync';

export const lastSyncKey = (userId) => `lastSync:${userId}`;

const toWire = ({ op_id, op, type, client_id, parent_client_id, base_version, data }) => ({
  op_id, op, type, client_id, parent_client_id, base_version, data,
});

/** Run `fn` holding the sync lock (where Web Locks exist; pages and workers both have them). */
export const withSyncLock = (fn) => {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  return locks?.request ? locks.request(SYNC_LOCK, fn) : fn();
};

/**
 * Send everything waiting for `userId`.
 * - `postSync(operations)` resolves to the server's results array.
 * - `fetchTree()` resolves to GET /surveys data (or rejects; that's fine).
 * - `onChunk()` is called after each reply has been reconciled.
 * Resolves to the number of operations sent. A network failure rejects, with
 * the outbox intact for the next attempt.
 */
export const syncOutbox = async ({ userId, postSync, fetchTree, onChunk }) => {
  let sentCount = 0;
  for (;;) {
    const outbox = await readOutbox(userId);
    if (!outbox.length) break;
    const sent = outbox.slice(0, CHUNK);
    const results = await postSync(sent.map(toWire));

    const [saved, species] = await Promise.all([readResponse(userId, '/surveys'), readResponse(userId, '/species')]);
    const snapshot = saved?.data || [];
    const names = new Map((species?.data || []).map((s) => [Number(s.id), s.name]));
    const view = applyOperations(snapshot, outbox, { speciesName: (id) => names.get(Number(id)) });
    const { snapshot: next, removeSeqs, problems } = reconcile({ snapshot, view, sent, results, waiting: outbox.slice(CHUNK) });
    await saveResponse(userId, '/surveys', next);
    await removeFromOutbox(removeSeqs);
    await addProblems(userId, problems);
    sentCount += sent.length;
    if (onChunk) await onChunk();
  }
  // Everything is on the server: pick up other people's changes too. If the
  // signal drops right now, the saved tree is still correct, just not fresh.
  try {
    const tree = await fetchTree();
    if (Array.isArray(tree)) await saveResponse(userId, '/surveys', tree);
  } catch {
    // Not fatal.
  }
  await saveMeta(lastSyncKey(userId), Date.now());
  return sentCount;
};
